import { observer } from "mobx-react-lite";
import { StructureTypeIcon } from "@/components/StructureTypeIcon";
import { networkTypeConfig } from "@/config/networkType";
import { useGeneralMapStore } from "../stores/GeneralMapStoreContext";
import { FilterGroup } from "@/components/FilterGroup";
import { GroupIcon } from "@/components/badges/GroupBadge/GroupIcon";
import { SearchInput } from "@/components/SearchInput";
import { cn } from "@/lib/utils";
import { CUSTOM_FILTERS } from "@/config/list";
import { ActivityFilter } from "./ActivityFilter";

const NetworkTypeChips = observer(() => {
	const { mapStore } = useGeneralMapStore();
	const { filters } = mapStore;

	return (
		<div className="space-y-1.5 pt-1">
			<span className="text-xs font-medium text-gray-700">Network</span>
			<div className="flex flex-wrap gap-1.5">
				{Object.entries(networkTypeConfig).map(([key, config]) => {
					const isSelected = filters.isSelected(CUSTOM_FILTERS.networkType, key);
					return (
						<button
							key={key}
							onClick={() => filters.toggle(CUSTOM_FILTERS.networkType, key)}
							className={cn(
								"inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium transition-colors border",
								isSelected
									? "border-transparent text-white"
									: "border-gray-200 text-gray-600 bg-white hover:bg-gray-50",
							)}
							style={
								isSelected ? { backgroundColor: config.bgColor, color: config.textColor } : undefined
							}
						>
							<span
								className="inline-block h-2 w-2 rounded-full"
								style={{ backgroundColor: config.bgColor }}
							/>
							{config.label}
						</button>
					);
				})}
			</div>
		</div>
	);
});

export const Filters = observer(() => {
	const generalMapStore = useGeneralMapStore();
	const { mapStore, structureTypes, structureGroups, viewPerspectiveStructures } = generalMapStore;
	const { filters } = mapStore;

	const structures = viewPerspectiveStructures;

	const typeItems =
		structureTypes.data?.items.map(type => ({
			label: type.name,
			count: structures.filter(structure => structure.type?.id === type.id).length,
			value: type.id,
			checked: filters.isSelected(CUSTOM_FILTERS.structureTypesIds, type.id),
		})) || [];

	const groupItems =
		structureGroups.data?.items.map(group => ({
			label: group.name,
			count: structures.filter(structure =>
				structure.groups?.some(structureGroup => structureGroup.id === group.id),
			).length,
			value: group.id,
			checked: filters.isSelected(CUSTOM_FILTERS.structureGroupIds, group.id),
		})) || [];

	return (
		<>
			<SearchInput
				value={(filters.getFilter(CUSTOM_FILTERS.search) as string | undefined) ?? ""}
				onChange={value => filters.setFilter(CUSTOM_FILTERS.search, value)}
				placeholder="Search by name…"
				className="w-full"
			/>

			<NetworkTypeChips />

			<ActivityFilter />

			<FilterGroup
				title="Types"
				items={typeItems}
				onToggle={value => filters.toggle(CUSTOM_FILTERS.structureTypesIds, value)}
				selectedCount={filters.selectedIds(CUSTOM_FILTERS.structureTypesIds).size}
				renderItem={item => (
					<StructureTypeIcon typeId={item.value as Dryp.StructureTypeId} showLabel size={16} />
				)}
				onSelectAll={ids => filters.selectAll(CUSTOM_FILTERS.structureTypesIds, ids)}
				onUnselectAll={() => filters.clear(CUSTOM_FILTERS.structureTypesIds)}
			/>

			<FilterGroup
				title="Groups"
				items={groupItems}
				onToggle={value => filters.toggle(CUSTOM_FILTERS.structureGroupIds, value)}
				selectedCount={filters.selectedIds(CUSTOM_FILTERS.structureGroupIds).size}
				onSelectAll={ids => filters.selectAll(CUSTOM_FILTERS.structureGroupIds, ids)}
				onUnselectAll={() => filters.clear(CUSTOM_FILTERS.structureGroupIds)}
				renderItem={item => (
					<div className="flex items-center gap-1.5 text-xs">
						<GroupIcon className="h-3.5 w-3.5 text-blue-400" />
						{item.label}
					</div>
				)}
			/>
		</>
	);
});
